import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactElement,
} from 'react';
import classNames from 'classnames/bind';

import SearchInput from './SearchInput';
import FilterToggle from './FilterToggle';
import FilterFooter from './FilterFooter';
import { FilterCategory } from './FilterColumn';
import Styles from './SearchAndFilter.styles';

const context = classNames.bind(Styles);

export type SearchAndFilterProps = {
  children?: ReactElement<FilterCategory>[];
  keyboardShortcuts?: boolean;
  loading?: boolean;
  numberOfResults?: number;
  onClearFilters?: () => void;
  onSearch: () => void;
  overlapContentBelow?: boolean;
  searchOnTextChange?: boolean;
};

const SearchAndFilter: React.FC<SearchAndFilterProps> = ({
  children = [],
  keyboardShortcuts = false,
  loading = false,
  numberOfResults = null,
  onClearFilters = () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  onSearch,
  overlapContentBelow = false,
  searchOnTextChange = false,
}) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const keydownHandler = useCallback((event) => {
    if (isTyping || !keyboardShortcuts) return;

    if (event.key === 'f' && children.length > 0) {
      setShowFilters((currentlyShowingFiles) => !currentlyShowingFiles);
    }

    if (event.key === 's') {
      event.preventDefault();
      searchInputRef.current?.focus();
      setIsTyping(true);
    }
  }, [children, isTyping, keyboardShortcuts]);

  const numberOfActiveFilter = useMemo(() => {
    if (!(children && children.length > 0)) {
      return 0;
    }

    // Increase the number for all filters with "active" set to true
    return children.reduce((numberOfEnabledFilters, child) => (
      numberOfEnabledFilters + child?.props.items.count((filter) => filter?.active ?? false)
    ), 0);
  }, [children]);

  useEffect(() => {
    window.addEventListener('keydown', keydownHandler);

    return () => {
      window.removeEventListener('keydown', keydownHandler);
    };
  }, [keydownHandler]);

  return (
    <div className={context({
      'position-helper': overlapContentBelow,
    })}
    >
      <div
        className={context('default', 'bg-white', { expanded: showFilters })}
        data-qa="manual-search-filter-wrapper"
      >
        <div className={Styles['search-wrapper']}>
          <SearchInput
            blur={() => setIsTyping(false)}
            focus={() => setIsTyping(true)}
            keyboardShortcuts={keyboardShortcuts}
            loading={loading}
            numberOfResults={numberOfResults}
            onSearch={onSearch}
            searchInputRef={searchInputRef}
            searchOnTextChange={searchOnTextChange}
          />

          { children?.length > 0 && (
            <FilterToggle
              activeFilters={numberOfActiveFilter}
              keyboardShortcuts={keyboardShortcuts}
              showFilters={showFilters}
              toggleFilterView={() => setShowFilters((!showFilters))}
            />
          )}
        </div>

        { children?.length > 0 && (
          <div
            className={context('filter-wrapper', { hidden: !showFilters })}
            data-qa="manual-search-filter-list"
          >
            <div className={`${Styles['columns-wrapper']} border-0 border-t border-solid border-gray-200`}>
              {children}
            </div>
            <FilterFooter
              activeFilters={numberOfActiveFilter}
              clearFilters={onClearFilters}
              closeFilters={() => setShowFilters(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchAndFilter;
